'use strict';
import FontAwesome from 'react-fontawesome';
import Http from 'axios';
import React from 'react';
import SupportInfo from '../SupportInfo.jsx';

/**
 * Array of options for months in a year
 */
const DateMonths = [
    ['January', '01'],
    ['February', '02'],
    ['March', '03'],
    ['April', '04'],
    ['May', '05'],
    ['June', '06'],
    ['July', '07'],
    ['August', '08'],
    ['September', '09'],
    ['October', '10'],
    ['November', '11'],
    ['December', '12']
];

/**
 * Array of options for days in a month
 */
const DateDays = [
    [1, '01'],
    [2, '02'],
    [3, '03'],
    [4, '04'],
    [5, '05'],
    [6, '06'],
    [7, '07'],
    [8, '08'],
    [9, '09'],
    [10, '10'],
    [11, '11'],
    [12, '12'],
    [13, '13'],
    [14, '14'],
    [15, '15'],
    [16, '16'],
    [17, '17'],
    [18, '18'],
    [19, '19'],
    [20, '20'],
    [21, '21'],
    [22, '22'],
    [23, '23'],
    [24, '24'],
    [25, '25'],
    [26, '26'],
    [27, '27'],
    [28, '28'],
    [29, '29'],
    [30, '30'],
    [31, '31']
];

/**
 * Array of year options
 */
const DateYears = [0].reduce(result => {
    // Calcuate a range of years for options
    let currentYear = new Date().getFullYear();
    let finalYear = currentYear - 120;

    // Iterate each year and create an key/value pair as an array
    for(; currentYear >= finalYear; currentYear--) {
        result.push([currentYear, currentYear]);
    }

    // Return the new result
    return result;
}, []);

/**
 * Presents directions and a form that users can fill out to recover
 * their campus ID if they forgot it.
 *
 * @export
 * @class ForgotId
 * @extends {React.Component}
 */
export default class ForgotId extends React.Component {

    constructor(props) {
        super(props);

        // Set default state
        this.state = {
            viewState: 'none'
        };

        // Bind non-React methods
        this.createTextField = this.createTextField.bind(this);
        this.renderDefaultView = this.renderDefaultView.bind(this);
        this.submitForm = this.submitForm.bind(this);
    }

    /**
     * Generate a <select> element with built-in bindings to update component
     * state each time the user selects a new value.
     *
     * @param {String} targetProperty Destination property in the component state for the selected value
     * @param {String} prompt Dummy value to display on first appearance (forces user to make a selection)
     * @param {Array} [values=[]] Array of key-value pairs (each as an array) for generating the <option> elements
     * @param {Number} [width=125] Width of the select box
     */
    createSelectField(targetProperty, prompt, values=[], width=125) {
        return <select
                className="form-control"
                style={{width: `${width}px`}}
                onChange={event => {
                    this.setState({ [targetProperty]: event.target.value });
                }}>
            <option>{prompt}</option>
            {values.map(valuePair => {
                return <option key={valuePair[1]} value={valuePair[1]}>{valuePair[0]}</option>;
            })};
        </select>;
    }

    /**
     * Generate an <input> element with built-in bindings to update component
     * each time the user types in a new value.
     *
     * @param {String} targetProperty Destination property in the component state for the selected value
     * @param {Number} [width=250] Width the text box
     * @param {String} [type="text"] Value for the type="..." attribute
     */
    createTextField(targetProperty, width=250, type="text") {
        return <input type={type} className="form-control" style={{width: `${width}px`}} onChange={event => {
            this.setState({ [targetProperty]: event.target.value });
        }} />;
    }

    /**
     * Submit the user information to the server to begin the campus ID recovery process
     */
    async submitForm() {
        // Collapse birth date into single string
        let birthDate = `${this.state.birthDateMonth}${this.state.birthDateDay}${this.state.birthDateYear}`;

        // Execute recovery request
        try {
            let response = await Http.post('/flow/recoverid', {
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                birthDate: birthDate,
                ssn: this.state.ssn
            });

            // Update component state
            this.setState(response.data);
        }
        catch(error) {
            this.setState({error: true});
        }
    }

    /**
     * Default view to display if no other view state is set
     */
    renderDefaultView() {
        return <div>
            <p>
                If you do not know your student/staff campuswide ID (CWID), please
                fill out the form below. If successfully authenticated, you will
                recieve a copy of your ID in all of the e-mail accounts associated
                with your staff or student profile. <strong>After receiving your
                ID, please return to this password reset service to try again.</strong>
            </p>

            <p>
                If you cannot use this form due to certain problems (for example:
                not knowing your SSN), then you will need to call us or visit
                the college in person.
            </p>

            <SupportInfo />

            <div>
                <div className="form-group">
                    <label>What is your legal first name?</label>
                    {this.createTextField('firstName')}
                </div>

                <div className="form-group">
                    <label>What is your legal last name?</label>
                    {this.createTextField('lastName')}
                </div>

                <div className="birth-date-section">
                    <label>What is your birth date?</label>
                    <div className="birth-date-field">
                        <label>Month</label>
                        {this.createSelectField('birthDateMonth', 'Choose Month', DateMonths)}
                    </div>
                    <div className="birth-date-field">
                        <label>Day</label>
                        {this.createSelectField('birthDateDay', 'Choose Day', DateDays)}
                    </div>
                    <div className="birth-date-field">
                        <label>Year</label>
                        {this.createSelectField('birthDateYear', 'Choose Year', DateYears)}
                    </div>
                </div>

                <div className="form-group">
                    <label>What are the last 4 digits of your SSN (social security number)?</label>
                    {this.createTextField('ssn', 125, 'password')}
                </div>

                <div>
                    <button className="btn btn-default" onClick={this.submitForm}>
                        Submit
                    </button>
                    <a className="btn btn-default" href="/resetpwd">
                        <i className="fa fa-refresh"></i> Start Over
                    </a>
                </div>
            </div>
        </div>;
    }

    render() {
        if(this.state.error) {
            return <div>
                <div className="alert alert-danger">
                    <FontAwesome name="warning" /> <strong>Error:</strong> Your identity could not be verified
                </div>

                <p>
                    We were not able to verify your identity. Please try again,
                    and make sure your first name, last name, birth date, and
                    social security number match our records.
                </p>
                <p>
                    If you cannot use this form due to certain problems  (for example:
                    not knowing your SSN), then you will need to call us or visit
                    the college in person.
                </p>

                <SupportInfo />

                <p>
                    <a className="btn btn-default" href="/resetpwd">
                        <i className="fa fa-refresh"></i> Start Over
                    </a>
                </p>
            </div>;
        }
        else if(this.state.viewState === 'success') {
            return <div>
                <div className="alert alert-success">
                    Recovery e-mail sent successfully
                </div>

                <p>
                    Check your e-mail inbox for a message sent by us that will have
                    your campus ID. After receiving your e-mail, if you need to reset
                    your password, click the 'Reset Password' button.
                </p>
                <p>
                    <a className="btn btn-default" href="/resetpwd">
                        <i className="fa fa-refresh"></i> Reset Password
                    </a>
                </p>
            </div>;
        }
        else {
            return this.renderDefaultView();
        }
    }

}


// WEBPACK FOOTER //
// ./src/react/views/ForgotId.jsx